import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertPromotionForm } from '../hooks/useUpsertPromotionForm';
import type { PromotionDto, UpsertPromotionDto } from '../types/PromotionDto';
import { UpsertPromotionForm } from './UpsertPromotionForm';

interface EditPromotionModalProps {
	promotion: UpsertPromotionDto;
	close: () => void;
	isOpened: boolean;
	updateTableValue: (p: PromotionDto) => void;
}

export function EditPromotionModal({
	promotion,
	isOpened,
	close,
	updateTableValue,
}: EditPromotionModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const [
		showBranchChangeModal,
		{ open: openBranchChangeModal, close: closeBranchChangeModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertPromotionForm(promotion);

	const newPromotionMutation = mutate<UpsertPromotionDto, PromotionDto>(
		`${organisation}/promotions/edit`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (newPromotionMutation.data?.value) {
			updateTableValue(newPromotionMutation.data.value);
			form.setInitialValues(inputs);
		}
		resetAndClose();
	};

	useEffect(() => {
		if (inputs.branchId !== promotion.branchId) openBranchChangeModal();
	}, [inputs.branchId]);

	const cancelBranchChange = () => {
		form.setFieldValue('branchId', promotion.branchId);
		closeBranchChangeModal();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Promotion'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newPromotionMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Promotion Edited!'}
					body={'A promotion has been edited. Click Ok to continue...'}
				/>
				<SmallModal
					isOpen={showBranchChangeModal}
					close={cancelBranchChange}
					onComplete={closeBranchChangeModal}
					title={'Branch Change!'}
					body={
						'Changing branch associated with promotion will cause all branch stocks to be removed. '
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newPromotionMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertPromotionForm form={form} />
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
